import type { KaiLiveToolStatus } from "./kaiTypes";

/** Patterns that must never appear in the chat status line. */
const INTERNAL_STATUS = [
  /^RAG:/i,
  /domain chunks/i,
  /top sim/i,
  /business rows/i,
  /Compacted history/i,
  /Step \d+ complete \(LLM/i,
];

/** Map backend agent-loop status lines to user-facing copy. */
const FRIENDLY_STATUS: [RegExp, string][] = [
  [/^Thinking…$/i, "Thinking about your question…"],
  [/^Analyzing results \(step \d+\)…$/i, "Finalizing your answer…"],
  [/^Double-checking the answer against the data…$/i, "Verifying the details…"],
  [/^Running \d+ reads in parallel…$/i, "Looking up the data…"],
  [/^Recovering from a temporary API error…$/i, "One moment…"],
  [/^Rate limited, retrying in \d+ms…$/i, "One moment…"],
  [/^Synthesizing the answer…$/i, "Putting together your answer…"],
  [/^Preparing your answer…$/i, "Almost ready…"],
];

export function sanitizeKaiStatusMessage(
  message: string,
  logger?: string,
): string {
  const trimmed = message.trim();
  if (!trimmed) return "";

  if (logger === "rag" || /^RAG:/i.test(trimmed)) {
    return "Fetching relevant documents…";
  }

  if (INTERNAL_STATUS.some((re) => re.test(trimmed))) {
    return "";
  }

  return trimmed;
}

export function formatKaiLiveStatusMessage(
  message: string,
  logger?: string,
): string {
  const sanitized = sanitizeKaiStatusMessage(message, logger);
  if (!sanitized) return "";

  for (const [pattern, friendly] of FRIENDLY_STATUS) {
    if (pattern.test(sanitized)) {
      return friendly;
    }
  }

  return sanitized;
}

export function allKaiToolsComplete(
  tools: { status: KaiLiveToolStatus }[],
): boolean {
  return tools.length > 0 && tools.every((tool) => tool.status !== "running");
}

export function anyKaiToolRunning(
  tools: { status: KaiLiveToolStatus }[],
): boolean {
  return tools.some((tool) => tool.status === "running");
}

export function kaiWorkingBubbleMessage(options: {
  mcpStatus: string;
  toolActivity: { status: KaiLiveToolStatus }[];
}): string {
  const formatted = formatKaiLiveStatusMessage(options.mcpStatus);
  if (formatted) return formatted;

  if (allKaiToolsComplete(options.toolActivity)) {
    return "Finalizing your answer…";
  }

  if (options.toolActivity.length > 0) {
    return anyKaiToolRunning(options.toolActivity)
      ? "Looking up the data…"
      : "Finalizing your answer…";
  }

  return "Thinking about your question…";
}

export function kaiStreamingFinalizingHint(options: {
  isGenerating: boolean;
  streamingContent: string;
  toolActivity: { status: KaiLiveToolStatus }[];
}): string | null {
  if (
    !options.isGenerating ||
    !options.streamingContent.trim() ||
    !allKaiToolsComplete(options.toolActivity)
  ) {
    return null;
  }

  return "Almost there — finishing your answer…";
}

export function kaiToolGroupStatusMessage(options: {
  mcpStatus: string;
  toolActivity: { status: KaiLiveToolStatus }[];
}): string {
  const formatted = formatKaiLiveStatusMessage(options.mcpStatus);
  if (formatted) return formatted;

  if (allKaiToolsComplete(options.toolActivity)) {
    return "Finalizing your answer…";
  }

  if (anyKaiToolRunning(options.toolActivity)) {
    return "Looking up the data…";
  }

  return "";
}
